import { useCallback, useMemo } from "react";

import { Button, ButtonProps, Tooltip } from "@mui/material";
import { ContentCut } from "@mui/icons-material";

import useEditVideoFile from "../../../backend/useEditVideoFile";
import { useAppContext } from "../../../hooks/useAppContext";
import useGetVideoInfo from "../../../hooks/useGetVideoInfo";
import removeAndShiftSubtitles from "../../../utils/removeAndShiftSubtitles";

// 1. Import the Autonomous Brain
import { useKernel } from "../../../hooks/AIKernel"; // Adjust relative path as needed

const VideoCutButton: React.FC<ButtonProps> = ({ ...buttonProps }) => {
  const {
    timelineStartTime,
    timelineEndTime,
    isProcessing,
    videoRef,
    subtitles,
    setSubtitles,
  } = useAppContext();
  const { duration } = useGetVideoInfo();
  const { cutVideo } = useEditVideoFile();

  // 2. Connect to the Agent's state and sensory inputs
  const { agentState, reportBehavior } = useKernel();

  // 3. Adaptive Theme Variables
  const isDarkMode = agentState?.theme === "theme-cinematic-dark";
  const highlightCTA = agentState?.ui?.highlightCTAs;

  // Improved strict checking for changes
  const hasChanges = useMemo(
    () => timelineStartTime > 0 || timelineEndTime < duration,
    [duration, timelineEndTime, timelineStartTime]
  );

  const handleApplychanges = useCallback(async () => {
    const operations: Array<[number, number]> = [];

    // The user wants to cut the middle segment out, keeping the beginning and the end
    if (timelineStartTime > 0) {
      operations.push([0, timelineStartTime]);
    }

    if (timelineEndTime < duration) {
      operations.push([timelineEndTime, duration]);
    }

    const resultBlobUrl = await cutVideo(operations);

    if (subtitles?.length) {
      const newSubtitles = removeAndShiftSubtitles(subtitles, [
        [timelineStartTime, timelineEndTime],
      ]);
      setSubtitles(newSubtitles);
    }

    if (!resultBlobUrl || !videoRef) {
      // TELEMETRY: Cut operation failed (Friction)
      if (reportBehavior) reportBehavior("video_cut_error", 2, "behavior");
      return;
    }

    // TELEMETRY: Successful manual editing application
    if (reportBehavior) reportBehavior("video_manual_cut_applied", 8, "interest");

    videoRef.src = resultBlobUrl;
  }, [
    cutVideo,
    duration,
    setSubtitles,
    subtitles,
    timelineEndTime,
    timelineStartTime,
    videoRef,
    reportBehavior
  ]);

  return (
    <Tooltip
      arrow
      disableFocusListener
      placement="top"
      title="Cuts the selected part of the video"
    >
      <span>
        <Button
          {...buttonProps}
          disabled={!hasChanges || isProcessing}
          startIcon={<ContentCut color="inherit" />}
          size="small"
          variant="contained"
          onClick={handleApplychanges}
          sx={{ 
            textTransform: "none",
            // ADAPTIVE UI: Pulse effect if the user is hovering in frustration and has changes ready to cut
            animation: highlightCTA && hasChanges && !isProcessing ? "pulse-cut 2s infinite" : "none",
            backgroundColor: isDarkMode ? "error.dark" : "error.main",
            color: "#fff",
            "&:hover": {
              backgroundColor: isDarkMode ? "error.main" : "error.dark",
            },
            "@keyframes pulse-cut": {
              "0%": { boxShadow: "0 0 0 0 rgba(211, 47, 47, 0.7)" },
              "70%": { boxShadow: "0 0 0 8px rgba(211, 47, 47, 0)" },
              "100%": { boxShadow: "0 0 0 0 rgba(211, 47, 47, 0)" }
            }
          }}
        >
          Cut
        </Button>
      </span>
    </Tooltip>
  );
};

export default VideoCutButton;